import { CommonModule } from '@angular/common'
import { Component, EventEmitter, Input, Output } from '@angular/core'
import {
    SESSION_DURATION_25_MINS,
    SESSION_DURATION_50_MINS,
    SESSION_DURATION_75_MINS,
    SessionActivityType,
} from '@app/core/_models'

import { FontAwesomeModule } from '@fortawesome/angular-fontawesome'
import { SessionSettingsService } from '@app/core/_services/session-settings.service'
import { SessionIndicatorIconsComponent } from '../session-indicator-icons/session-indicator-icons.component'
import { DstBoundaryService } from '@app/core/_services/dst-service'

// Define the interface for the session-related dimensions
interface SessionOutlineDimensions {
    outlineheight: number
}

// NOTE: Defining again here since importing this component in two
// different routes /dashboard and /sessions is causing a circular dependency
// on the calendar_day component. TODO: Move all constants to a shared file?
const NUM_TIMESLOTS_PER_DAY = 24 * 4 + 3
const TIMESLOT_HEIGHT = 48 // Each 15minutes slot is 48px high

const SESSION_DURATION_50_MINS_LABEL: string = '50m'
const SESSION_DURATION_25_MINS_LABEL: string = '25m'
const SESSION_DURATION_75_MINS_LABEL: string = '75m'

@Component({
    selector: 'fm-session-selector-outline',
    imports: [CommonModule, FontAwesomeModule, SessionIndicatorIconsComponent],
    templateUrl: './session-selector-outline.component.html',
    styleUrl: './session-selector-outline.component.scss',
})
export class SessionSelectorOutlineComponent {
    @Input() sessionDuration: number = SESSION_DURATION_25_MINS
    @Input() slotNumber: number | null = null
    @Input() sessionTime: number | null = null
    @Input() partnerAvailable: boolean = false
    @Input() isToday: boolean
    @Input() timezone: string
    @Output() timeslotselected = new EventEmitter<number>()

    public activityType: SessionActivityType
    public quietMode: boolean

    public outlineHeight: number = 0

    private sessionDimensionsMap: { [key: number]: SessionOutlineDimensions } = {
        [SESSION_DURATION_25_MINS]: {
            outlineheight: TIMESLOT_HEIGHT * 2,
        },
        [SESSION_DURATION_50_MINS]: {
            outlineheight: TIMESLOT_HEIGHT * 4,
        },
        [SESSION_DURATION_75_MINS]: {
            outlineheight: TIMESLOT_HEIGHT * 6,
        },
    }

    constructor(
        private dstBoundaryService: DstBoundaryService,
        public sessionSettingsService: SessionSettingsService,
    ) {
        this.determineIcons()
    }

    ngOnChanges() {
        if (this.sessionDuration) {
            const dstData = this.dstBoundaryService.intersectsSpringForward(
                this.timezone,
                this.sessionTime,
                this.sessionTime + this.sessionDuration,
            )

            let outlineHeight

            if (this.sessionDuration === SESSION_DURATION_75_MINS && this.slotNumber > 93) {
                outlineHeight = this.sessionDimensionsMap[SESSION_DURATION_50_MINS].outlineheight
            } else {
                outlineHeight = this.sessionDimensionsMap[this.sessionDuration].outlineheight
            }

            if (dstData.intersects) {
                outlineHeight += TIMESLOT_HEIGHT * (dstData.shiftMinutes / 15)

                if (outlineHeight / TIMESLOT_HEIGHT + this.slotNumber >= NUM_TIMESLOTS_PER_DAY) {
                    outlineHeight = (NUM_TIMESLOTS_PER_DAY - this.slotNumber - 1) * TIMESLOT_HEIGHT
                }
            }
            this.outlineHeight = outlineHeight
        }
        this.determineIcons()
    }

    private determineIcons() {
        this.activityType = this.sessionSettingsService.getSessionSettingActivityType()
        this.quietMode = this.sessionSettingsService.quietModeEnabled()
    }
}
